import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { BookOpen, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { getKwOverview, getKwOverviewGlobal, resetParcelKw } from "../api";
import { fmtDateTime } from "../utils";
import type { KwOverview, KwOverviewRow, ParcelExportFilters } from "../types";
import { KwAnalysisModal } from "./KwAnalysisModal";
import { EmptyState, TableShell, Th } from "./ui";

// Zakładka „KW": każda działka z numerem księgi + status pobrania i analizy.
// „Analizuj KW" otwiera to samo okno co na działce (wgranie PDF, EKW, wtyczka,
// akcept/odrzut rozbieżności właścicieli); „Resetuj" czyści dane KW działki do zera.
// Dwa tryby: projekt (KwTab) i globalny na stronie Działki (GlobalKwPanel — z kolumną
// przedsięwzięcia, na filtrach strony).

function StatusCell({ row }: { row: KwOverviewRow }) {
  if (!row.kw_valid) {
    return <span className="text-amber-600">wpis nie jest numerem księgi — popraw ręcznie</span>;
  }
  if (row.has_file && row.analyzed_at) {
    return (
      <span className="text-emerald-600">
        pobrana i przeanalizowana · {fmtDateTime(row.analyzed_at)}
        {row.sections_count ? ` · ${row.sections_count} dz.` : ""}
        {row.owners_count ? ` · ${row.owners_count} właśc.` : ""}
      </span>
    );
  }
  if (row.has_file) {
    return <span className="text-amber-600">plik jest, analiza AI jeszcze nie przeszła</span>;
  }
  if (row.not_found) {
    return <span className="text-red-600">portal: księga nie istnieje — numer błędny</span>;
  }
  if (row.attempts > 0) {
    return (
      <span className="text-red-600" title={row.last_attempt_detail}>
        {row.attempts} {row.attempts === 1 ? "nieudana próba" : "nieudane próby"}
        {row.last_attempt_at
          ? ` · ${row.last_attempt_outcome_label} · ${fmtDateTime(row.last_attempt_at)}`
          : ""}
      </span>
    );
  }
  return <span className="text-[var(--color-text-muted)]">jeszcze nie próbowano</span>;
}

function DiffCell({ row }: { row: KwOverviewRow }) {
  const d = row.diff;
  if (!d) return <span className="text-[var(--color-text-muted)]">—</span>;
  const open = d.only_in_kw + d.only_in_db;
  if (open > 0) {
    return (
      <span className="text-amber-600">
        {open} {open === 1 ? "rozbieżność" : "rozbieżności"}
        {d.accepted ? ` · ${d.accepted} zaakc.` : ""}
      </span>
    );
  }
  if (d.db_empty) return <span className="text-amber-600">brak właścicieli na działce</span>;
  return (
    <span className="text-emerald-600">zgodni{d.accepted ? ` · ${d.accepted} zaakc.` : ""}</span>
  );
}

function KwOverviewView({
  data, isLoading, queryKey, showProject, readOnly = false,
}: {
  data: KwOverview | undefined;
  isLoading: boolean;
  queryKey: QueryKey;
  showProject: boolean;
  /** Minimalny dostęp do DLC_01: bez linku do projektu, okna księgi i resetu. */
  readOnly?: boolean;
}) {
  const qc = useQueryClient();
  const [openRow, setOpenRow] = useState<KwOverviewRow | null>(null);
  const reset = useMutation({
    mutationFn: (parcelId: string) => resetParcelKw(parcelId),
    onSuccess: (_r, parcelId) => {
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ["pp", "parcel-kw", parcelId] });
    },
  });

  if (isLoading) return <div className="py-12 flex justify-center"><Spinner /></div>;
  const items = data?.items ?? [];
  const stats = data?.stats;

  return (
    <div className="space-y-3">
      {stats && (
        <p className="font-mono text-[0.65rem] uppercase tracking-widest text-[var(--color-text-muted)]">
          Z numerem KW: {stats.total} · pobrane: {stats.done} · nietknięte: {stats.untried}
          {stats.invalid ? ` · złe wpisy: ${stats.invalid}` : ""}
          {stats.truncated ? " · pokazuję pierwszy 1000 — zawęź filtry" : ""}
        </p>
      )}
      {items.length === 0 ? (
        <EmptyState>Żadna działka nie ma wpisanego numeru księgi wieczystej.</EmptyState>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Działka</Th>
              {showProject && <Th>Przedsięwzięcie</Th>}
              <Th>Nr KW</Th>
              <Th>Status</Th>
              <Th>Rozbieżności właścicieli</Th>
              {!readOnly && <Th className="text-right">Akcje</Th>}
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr
                key={row.parcel}
                className="border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)]"
              >
                <td className="px-3 py-2 font-mono whitespace-nowrap">{row.teryt}</td>
                {showProject && (
                  <td className="px-3 py-2 max-w-[220px]">
                    {readOnly ? (
                      <span className="block truncate" title={row.project_name}>
                        {row.project_name || "—"}
                      </span>
                    ) : (
                      <Link
                        to={`/power-projects/${row.project_id}?tab=kw`}
                        className="text-[var(--color-primary)] hover:underline block truncate"
                        title={row.project_name}
                      >
                        {row.project_name || "—"}
                      </Link>
                    )}
                  </td>
                )}
                <td className="px-3 py-2 font-mono whitespace-nowrap">{row.kw_number}</td>
                <td className="px-3 py-2"><StatusCell row={row} /></td>
                <td className="px-3 py-2"><DiffCell row={row} /></td>
                {!readOnly && (
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <span className="inline-flex items-center gap-3">
                      <button
                        className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                        onClick={() => setOpenRow(row)}
                        title="Okno księgi: wgranie PDF, EKW, pobranie wtyczką, analiza, akcept rozbieżności"
                      >
                        <BookOpen size={13} /> Analizuj KW
                      </button>
                      <button
                        className="text-[var(--color-text-muted)] hover:text-red-600 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={!row.has_file || reset.isPending}
                        onClick={() => {
                          if (window.confirm(
                            `Usunąć WSZYSTKIE dane KW działki ${row.teryt} (treści, analizy, PDF-y działów)? `
                            + "Działka wróci do kolejki automatu jak nietknięta.",
                          )) reset.mutate(row.parcel);
                        }}
                        title="Resetuj dane z KW"
                      >
                        <Trash2 size={13} /> Resetuj
                      </button>
                    </span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}

      {openRow && !readOnly && (
        <KwAnalysisModal
          parcel={{ id: openRow.parcel, teryt: openRow.teryt, kw_number: openRow.kw_number }}
          onClose={() => { setOpenRow(null); qc.invalidateQueries({ queryKey }); }}
        />
      )}
    </div>
  );
}

export function KwTab({ connection }: { connection: { id: string } }) {
  const queryKey: QueryKey = ["pp", "kw-overview", connection.id];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => getKwOverview(connection.id),
  });
  return <KwOverviewView data={data} isLoading={isLoading} queryKey={queryKey} showProject={false} />;
}

// Strona Działki (globalna): te same wiersze dla aktualnych filtrów strony.
export function GlobalKwPanel({
  filters, readOnly = false,
}: {
  filters: ParcelExportFilters;
  readOnly?: boolean;
}) {
  const queryKey: QueryKey = ["pp", "kw-overview-global", filters];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => getKwOverviewGlobal(filters),
  });
  return (
    <KwOverviewView
      data={data} isLoading={isLoading} queryKey={queryKey} showProject readOnly={readOnly}
    />
  );
}
